import { useEffect, useState } from "react";
import { usePaginationState } from "../../App/Components/Table/State/PaginationState";
import { CampaignActiveStatus, CampaignSortBy } from "../../App/Enums/CampaignTypes";
import { useAllCampaigns } from "../../App/Queries/CampaignQueries";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import { useCampaignListingStore } from "../../App/State/Stores/CampaignListing";
import { ICampaign } from "../../App/Typings/Campaigns";
import useDeleteCampaignDialogState from "../../CampaignProfile/State/DeleteCampaignDialogState";
import { MaxSearchLength } from "../../CandidateGroups/State/CandidateGroupsState";
import DialogState from "./DialogState";
import EmbedScriptState from "./EmbedScriptState";
import ViewCampaignState from "./ViewCampaignState";

export function useManageCampaignsState() {
  const { dialogProps, handleDisplayDialog, handleClearDialog } = DialogState();
  const paginationState = usePaginationState({ sortBy: CampaignSortBy.Name }, "campaigns");
  const [searchInput, setSearchInput] = useState<string>(paginationState.getters.search);
  const [searchInputError, setSearchInputError] = useState<string>("");
  const { view, setView } = useCampaignListingStore();

  const {
    data: campaignPagination,
    error,
    isLoading,
  } = useAllCampaigns(
    paginationState.getters.getQueryString({
      page: paginationState.getters.page + 1,
    }),
  );

  const { handleSubmitDeleteCampaign, handleOpenDeleteDialog } = useDeleteCampaignDialogState(
    handleDisplayDialog,
    handleClearDialog,
  );
  const { handleOpenEmbedScriptDialog } = EmbedScriptState(handleDisplayDialog, handleClearDialog);
  const { handleOpenViewCampaignDialog } = ViewCampaignState();

  const handleChangeSortBy = (sortBy: CampaignSortBy) => {
    paginationState.handlers.handleRequestSort(sortBy.toString());
  };

  const handleChangeSortDirection = (sortDirection: "asc" | "desc") => {
    paginationState.setters.setSortDirection(sortDirection);
    paginationState.setters.setPage(0);
  };

  const handleSearchFilter = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > MaxSearchLength) {
      setSearchInputError(`Search input must be less than or equal to ${MaxSearchLength} characters`);
    } else {
      setSearchInputError("");
    }
    setSearchInput(e.target.value);
  }, 300);

  const handleChangeView = (view: "card" | "list") => {
    localStorage.removeItem("campaigns-custom-table-state");
    setView(view);
  };

  useEffect(() => {
    if (searchInput.length <= MaxSearchLength && searchInput !== paginationState.getters.search) {
      paginationState.handlers.handleSearchChange({
        target: { value: searchInput },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, paginationState.getters.search]);

  return {
    getters: {
      dialogProps,
      searchInputError,
      paginationState,
      campaignPagination,
      error,
      isLoading,
      view,
    },
    handlers: {
      handleSubmitDeleteCampaign,
      handleOpenDeleteDialog,
      handleOpenEmbedScriptDialog,
      handleOpenViewCampaignDialog,
      handleDisplayDialog,
      handleClearDialog,
      handleSearchFilter,
      handleChangeSortBy,
      handleChangeSortDirection,
      handleChangeView,
    },
  } as const;
}

const isTimeBased = (campaign: ICampaign) => {
  if (campaign.ongoing || !campaign.startDate || !campaign.endDate) {
    return false;
  }
  return true;
};

export const matchesCurrentDate = (campaign: ICampaign) => {
  if (isTimeBased(campaign)) {
    const now = new Date();
    const startDate = new Date(campaign.startDate);
    const endDate = new Date(campaign.endDate);
    return startDate <= now && endDate >= now;
  }
  return false;
};

export const matchesFutureDate = (campaign: ICampaign) => {
  if (isTimeBased(campaign)) {
    const now = new Date();
    const startDate = new Date(campaign.startDate);
    return startDate > now;
  }
  return false;
};

export const matchesPastDate = (campaign: ICampaign) => {
  if (isTimeBased(campaign)) {
    const now = new Date();
    const endDate = new Date(campaign.endDate);
    return endDate < now;
  }
  return false;
};

export function getCampaignStatus(campaign: ICampaign) {
  if (campaign.ongoing) {
    return "Ongoing";
  }
  if (matchesCurrentDate(campaign)) {
    return "Short-Term";
  }
  if (matchesFutureDate(campaign)) {
    return "Scheduled";
  }
  if (matchesPastDate(campaign)) {
    return "Expired";
  }
  return "---";
}

export function getCampaignActiveStatus(campaign: ICampaign) {
  if (matchesFutureDate(campaign)) {
    return CampaignActiveStatus.Scheduled;
  }
  if (campaign.ongoing || matchesCurrentDate(campaign)) {
    return CampaignActiveStatus.Active;
  }
  return CampaignActiveStatus.Inactive;
}

export const filterOngoing = (campaigns: ICampaign[]) =>
  campaigns.filter((campaign: ICampaign) => campaign.ongoing);

export const filterActive = (campaigns: ICampaign[]) => campaigns.filter(matchesCurrentDate);

export const filterScheduled = (campaigns: ICampaign[]) => campaigns.filter(matchesFutureDate);

export const filterExpired = (campaigns: ICampaign[]) => campaigns.filter(matchesPastDate);
